import { handleBreakpoints, type ResponsiveStyleValue } from '@mui/system';
import { REDUCED_MOTION_MEDIA_QUERY } from '@packages/shared-ui';
import { makeStyles } from '@/styles/theme/tss-react';
import { darkOrLightStyle } from '@/utils/styles';
import { DropdownSize } from './Dropdown';

const useStyles = makeStyles<{
  size: ResponsiveStyleValue<DropdownSize>;
  isOnDark: boolean;
}>({
  name: 'Dropdown',
})(({ brandColors, typography, type, breakpoints }, { size, isOnDark }) => {
  const backgroundColor = brandColors.white;

  const selectTextColor = brandColors.tintsNeutral20;

  const labelColor = darkOrLightStyle({ isOnDark })(
    brandColors.white,
    brandColors.tintsNeutral20
  );

  const helperColor = darkOrLightStyle({ isOnDark })(
    brandColors.white,
    brandColors.tintsNeutral20
  );

  const defaultBorderColor = brandColors.tintsNeutral30;

  const hoverBorderColor = brandColors.black;

  const focusBorderColor = brandColors.chargeBlue;

  const errorColor = darkOrLightStyle({ isOnDark })(
    brandColors.semanticColorsError60,
    brandColors.semanticColorsError50
  );

  const iconColor = brandColors.black;

  const getSizeStyles = (size: DropdownSize) => {
    switch (size) {
      case DropdownSize.SM:
        return {
          '& .MuiSelect-select': {
            padding: `${typography.pxToRem(10)} ${typography.pxToRem(16)}`,
          },
        };
      case DropdownSize.LG:
        return {
          '& .MuiSelect-select': {
            padding: typography.pxToRem(16),
          },
        };

      default:
        return {};
    }
  };

  return {
    wrapper: {
      display: 'flex',
      gap: typography.pxToRem(9),
      '& .MuiFormHelperText-root': {
        marginLeft: 0,
      },
    },
    label: {
      ...type['14_regular'],
      display: 'flex',
      alignItems: 'center',
      gap: typography.pxToRem(8),
      color: labelColor,
      '&.Mui-disabled': {
        opacity: '0.4',
        color: labelColor,
      },
      '& svg': {
        color: brandColors.black,
      },
    },
    labelHidden: {
      visibility: 'hidden',
      height: 0,
    },
    placeholder: {
      color: selectTextColor,
    },
    menu: {
      '& .MuiMenuItem-root': {
        padding: `${typography.pxToRem(12)} ${typography.pxToRem(16)}`,
        '&:hover': {
          backgroundColor: brandColors.tintsChargeBlue80,
        },
      },
      '& .Mui-selected': { backgroundColor: brandColors.tintsMediumBlue80 },
      '& .Mui-focusVisible': {
        backgroundColor: 'initial',
      },
    },
    base: {
      '& .MuiSelect-select': {
        backgroundColor,
        color: selectTextColor,
      },
      '& legend': { display: 'none' },
      '& .MuiFormHelperText-root': {
        marginTop: typography.pxToRem(6),
        ...type['14_regular'],
        color: helperColor,
        '&.Mui-error': {
          color: errorColor,
        },
      },
      '& .MuiOutlinedInput-root': {
        transition: 'border-color 0.2s ease, border-width 0.2s ease',
        [REDUCED_MOTION_MEDIA_QUERY]: {
          transition: 'none',
        },

        '& fieldset': {
          top: 0,
          borderRadius: typography.pxToRem(8),
          border: `${typography.pxToRem(1)} solid ${defaultBorderColor}`,
        },

        // HOVER
        '&:hover fieldset': {
          borderColor: hoverBorderColor,
        },

        // FOCUS
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderWidth: typography.pxToRem(1.5),
          borderColor: focusBorderColor,
        },

        // ERROR always wins
        '&.Mui-error fieldset': {
          borderWidth: typography.pxToRem(1.5),
          borderColor: errorColor,
        },
        // Ensure error style overrides focus style when both are applied
        '&.Mui-error.Mui-focused fieldset': {
          borderWidth: typography.pxToRem(1.5),
          borderColor: errorColor,
        },

        // DISABLED always wins
        '&.Mui-disabled fieldset': {
          borderColor: defaultBorderColor,
          opacity: '0.4',
        },
      },
      '& .MuiSelect-icon': {
        marginRight: typography.pxToRem(16),
        width: `${typography.pxToRem(16)}!important`,
        right: 0,
        color: iconColor,
        transition: 'opacity 0.2s ease, color 0.2s ease',
        [REDUCED_MOTION_MEDIA_QUERY]: {
          transition: 'none',
        },
        '&&': {
          pointerEvents: 'none',
          position: 'absolute',
          top: 'auto',
          color: 'inherit',
        },
      },
      '& .MuiOutlinedInput-root.Mui-disabled .MuiSelect-icon': {
        opacity: 0.4,
      },
      '& .MuiSelect-select.Mui-disabled': {
        '-webkit-text-fill-color': selectTextColor,
        opacity: 0.4,
      },
    },
    responsiveSizes: handleBreakpoints(
      { theme: { breakpoints } },
      size,
      (resolvedSize) => getSizeStyles(resolvedSize as DropdownSize)
    ),
  };
});

export default useStyles;
